import React, { useState } from "react";
import PropTypes from "prop-types";
import { Card } from "react-bootstrap";
import ServerErrorAlert from "../profiles/ServerErrorAlert";

const URL_MAX_LENGTH = 500;
const TITLE_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;

// Mirrors AppConf::Regex::URL_RB (`\A(?:http(?:s)?://).+\z`) -- a client-side hint only. The server is
// authoritative; any mismatch this misses still surfaces via a 422 through ServerErrorAlert below.
const URL_FORMAT = /^https?:\/\/.+$/i;

const ITEM_TYPES = [
  { value: "article", label: "Article" },
  { value: "video", label: "Video" },
  { value: "whitepaper", label: "Whitepaper" },
  { value: "case_study", label: "Case Study" },
  { value: "podcast", label: "Podcast" },
];

const MEDIA_ITEM_ERROR_LABELS = {
  url: "URL",
  title: "Title",
  description: "Description",
  image_url: "Image URL",
  item_type: "Type",
};

const blankFields = { url: "", title: "", description: "", item_type: "article", image_url: "" };

function fieldsFrom(mediaItem) {
  return {
    url: mediaItem?.url ?? "",
    title: mediaItem?.title ?? "",
    description: mediaItem?.description ?? "",
    item_type: mediaItem?.item_type_string ?? mediaItem?.item_type ?? "article",
    image_url: mediaItem?.image_url ?? "",
  };
}

// The Add/edit form for a single media item -- a Card matching MediaItem's own chassis, swapped in for
// the display card while adding or editing. `onSave` is expected to THROW on failure (the
// media_items.js client always throws on a non-2xx or a network rejection via throwOnApiError), which
// is what keeps the editor open with the entered fields intact rather than closing on a save that
// didn't happen.
function MediaItemEditor({ initialMediaItem = blankFields, onSave, onClose, disabled = false }) {
  const [fields, setFields] = useState(fieldsFrom(initialMediaItem));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field) => (event) => {
    // React nullifies the synthetic event's fields once the handler returns, so the value has to be
    // read out here rather than inside the setFields updater (which can run after that happens).
    const { value } = event.currentTarget;
    setFields((prev) => ({ ...prev, [field]: value }));
  };

  const trimmedUrl = fields.url.trim();
  const trimmedTitle = fields.title.trim();
  const trimmedDescription = fields.description.trim();
  const trimmedImageUrl = fields.image_url.trim();

  const urlInvalid = trimmedUrl.length === 0 || trimmedUrl.length > URL_MAX_LENGTH || !URL_FORMAT.test(trimmedUrl);
  const titleInvalid = trimmedTitle.length > TITLE_MAX_LENGTH;
  const descriptionInvalid = trimmedDescription.length > DESCRIPTION_MAX_LENGTH;
  const imageUrlInvalid =
    trimmedImageUrl.length > 0 && (trimmedImageUrl.length > URL_MAX_LENGTH || !URL_FORMAT.test(trimmedImageUrl));

  const invalid = urlInvalid || titleInvalid || descriptionInvalid || imageUrlInvalid;

  const handleSave = async () => {
    if (invalid || saving || disabled) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({
        url: trimmedUrl,
        // Optional fields go as null rather than "" when blank -- id_media_items' format validators
        // use allow_nil: true, which does NOT cover an empty string, so a blank image_url sent as ""
        // would fail server-side format validation instead of being accepted as "none given".
        title: trimmedTitle || null,
        description: trimmedDescription || null,
        item_type: fields.item_type,
        image_url: trimmedImageUrl || null,
      });
      onClose();
    } catch (e) {
      // Editor stays open, entered fields are untouched (fields state is never reset here), and the
      // error renders inline -- a failed save must never look like a save that happened.
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-4">
      <Card.Body>
        <ServerErrorAlert generalError={error} labelMap={MEDIA_ITEM_ERROR_LABELS} />
        <div className="mb-2">
          <label className="form-label" htmlFor="media-item-url">
            URL
          </label>
          <input
            id="media-item-url"
            type="text"
            className="form-control"
            value={fields.url}
            onChange={handleChange("url")}
            disabled={saving || disabled}
            maxLength={URL_MAX_LENGTH}
          />
        </div>
        <div className="mb-2">
          <label className="form-label" htmlFor="media-item-title">
            Title
          </label>
          <input
            id="media-item-title"
            type="text"
            className="form-control"
            value={fields.title}
            onChange={handleChange("title")}
            disabled={saving || disabled}
            maxLength={TITLE_MAX_LENGTH}
          />
        </div>
        <div className="mb-2">
          <label className="form-label" htmlFor="media-item-description">
            Description
          </label>
          <textarea
            id="media-item-description"
            className="form-control"
            rows={3}
            value={fields.description}
            onChange={handleChange("description")}
            disabled={saving || disabled}
            maxLength={DESCRIPTION_MAX_LENGTH}
          />
        </div>
        <div className="mb-2">
          <label className="form-label" htmlFor="media-item-type">
            Type
          </label>
          <select
            id="media-item-type"
            className="form-select"
            value={fields.item_type}
            onChange={handleChange("item_type")}
            disabled={saving || disabled}
          >
            {ITEM_TYPES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="mb-2">
          <label className="form-label" htmlFor="media-item-image-url">
            Image URL
          </label>
          <input
            id="media-item-image-url"
            type="text"
            className="form-control"
            value={fields.image_url}
            onChange={handleChange("image_url")}
            disabled={saving || disabled}
            maxLength={URL_MAX_LENGTH}
          />
        </div>
      </Card.Body>
      <Card.Footer className="d-flex justify-content-end">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary d-flex me-2"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary d-flex"
          onClick={handleSave}
          disabled={invalid || saving || disabled}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </Card.Footer>
    </Card>
  );
}

MediaItemEditor.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  initialMediaItem: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default MediaItemEditor;
